import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useRunCmsEligibilityCheck, useCmsSubmissionPoll, useCaptureCmsBaseline } from '../state/data/cms';
import { CmsResultCodes, freshTerminalRow } from '@/logic/cmsResultCodes';
import { scheduleInvalidate } from '@/state/data/_shared/wf-race';
import { TASK_KEYS } from '@/state/data/task/keys';
import { CMS_KEYS } from '@/state/data/cms/keys';
import { alignmentTaskNote } from '@/logic/eligibility';
import { LoadingPanel, VerdictBanner, Section, Callout } from './cms/cmsModalParts';

// Eligibility submit is fire-and-poll: apiv2 acks cms_eligibility_requested at
// the ~5s SYNC_TIMEOUT, but WF12's CMS poll resolves in ~12-16s — so the verdict
// lands on the cms_submissions row, not the event response. Cap mirrors the
// enrollment funnel's 30s. Preview stays synchronous (no CMS call → fast).
const POLL_CAP_MS = 30_000;
const EMPTY_IDS = new Set();

/**
 * Operator-triggered CMS eligibility check (Tier 4 cms_eligibility_requested).
 * WF12 applies the side effects on success — auto-completes onb_eligibility_check only
 * (the clinical exclusion screen stays a clinician review) and creates the onb_cms_alignment
 * task ONLY if the baseline labs review has already resolved; otherwise it defers and WF17
 * creates it when that review is closed out. The modal just surfaces the result.
 */

/* ── Internal task-template-code → human label map ─────────────────────── */
const TEMPLATE_LABELS = {
  'onb_eligibility_check': 'Eligibility verification',
  'onb_clinical_exclusion_screen': 'Clinical exclusion screening',
  'onb_cms_alignment': 'CMS alignment submission',
};

const labelFor = (code) => TEMPLATE_LABELS[code] || code;

/* ── Loading-step lists (timed reveal) ───────────────────────────────────── */
const PREVIEW_LOADING_STEPS = [
  { at: 0,    label: 'Reading patient + insurance + care plan' },
  { at: 1500, label: 'Building FHIR Parameters payload' },
  { at: 3500, label: 'Preparing for operator review' },
];
const SUBMIT_LOADING_STEPS = [
  { at: 0,    label: 'Authenticating with CMS' },
  { at: 1500, label: 'POSTing to /Patient/$check-eligibility' },
  { at: 6000, label: 'Reviewing CMS response' },
];

/* ── ACCESS Track CodeSystem → display ──────────────────────────────────── */
const TRACK_LABELS = {
  eckm: 'Early Cardio-Kidney-Metabolic (eCKM)',
  ckm:  'Cardio-Kidney-Metabolic (CKM)',
  msk:  'Musculoskeletal (MSK)',
  bh:   'Behavioral Health (BH)',
};

export default function CmsEligibilityModal({ patient, carePeriodId, taskId, onClose, onSaved }) {
  // 'preview-loading' → WF12 builds FHIR Parameters (no CMS call)
  // 'preview'         → Operator reviews payload, confirms or cancels
  // 'submit-loading'  → WF12 actually POSTs to CMS
  // 'result'          → Verdict (existing 11-code map)
  // 'error'           → Network / transport failure
  const [phase, setPhase] = useState('preview-loading');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [errorKind, setErrorKind] = useState('network');
  const [stepIndex, setStepIndex] = useState(0);
  const [copied, setCopied] = useState(null);
  const [resultVisible, setResultVisible] = useState(false);
  const [showRawJson, setShowRawJson] = useState(false);
  // Skew-immune freshness: correlation_ids that existed before we fired submit.
  // null until captured — gates the poll on (phase submit-loading AND captured).
  const [baselineIds, setBaselineIds] = useState(null);
  const aliveRef = useRef(true);
  const capTimerRef = useRef(null);
  const settledRef = useRef(false); // verdict OR cap resolved — keeps both paths idempotent
  const qc = useQueryClient();
  const eligibilityMut = useRunCmsEligibilityCheck();
  // mutateAsync is the stable handle; the wrapper object is not.
  const runEligibility = eligibilityMut.mutateAsync;
  const captureBaseline = useCaptureCmsBaseline('eligibility');
  const poll = useCmsSubmissionPoll('eligibility', String(patient.userid ?? ''), {
    enabled: phase === 'submit-loading' && !!baselineIds,
    baselineIds: baselineIds || EMPTY_IDS,
  });

  // Build the verdict `result` the render expects from a terminal poll row.
  const rowToResult = useCallback((row) => ({
    result_code: row.result_code,
    display: row.result_display,
    reason: row.result_display,
    eligible: CmsResultCodes.isSuccess(row.result_code),
    checked_at: row.finished_at || row.created || row.created_at || null,
    mbi_last4: row.mbi_last4,
  }), []);

  // PREVIEW — synchronous (WF12 builds the FHIR payload, no CMS call → fast,
  // returns inside the apiv2 sync window). SUBMIT — fire-and-poll (below).
  const runPreview = useCallback(async () => {
    setPhase('preview-loading');
    setError(null); setErrorKind('network'); setStepIndex(0);
    setResultVisible(false); setShowRawJson(false);
    try {
      const res = await runEligibility({ withingsUserId: patient.userid, carePeriodId, taskId, mode: 'preview' });
      if (!aliveRef.current) return;
      setResult(res);
      setPhase('preview');
      requestAnimationFrame(() => { if (aliveRef.current) setResultVisible(true); });
    } catch (err) {
      if (!aliveRef.current) return;
      const msg = err.message || 'CMS eligibility preview failed';
      const kind = /auth|bearer|token|401|forbidden|403/i.test(msg) ? 'auth' : 'network';
      setError(msg); setErrorKind(kind); setPhase('error');
    }
  }, [patient.userid, carePeriodId, taskId, runEligibility]);

  // SUBMIT — snapshot existing rows, fire (ignore the ~5s ack), then the poll
  // effect below resolves the verdict from the fresh cms_submissions row.
  const submitCheck = useCallback(async () => {
    setError(null); setErrorKind('network'); setStepIndex(0);
    setResultVisible(false); setShowRawJson(false); setResult(null);
    setPhase('submit-loading');
    const baseline = await captureBaseline(String(patient.userid));
    if (!aliveRef.current) return;
    settledRef.current = false;
    setBaselineIds(baseline); // enables the poll (gated on phase + captured)
    if (capTimerRef.current) clearTimeout(capTimerRef.current);
    capTimerRef.current = setTimeout(() => {
      // Fail-soft: CMS slower than the cap → pending ("care team will review").
      if (!aliveRef.current || settledRef.current) return;
      settledRef.current = true;
      setResult({ result_code: 'pending' });
      setPhase('result');
      requestAnimationFrame(() => { if (aliveRef.current) setResultVisible(true); });
    }, POLL_CAP_MS);
    // Fire; the verdict comes from the poll, not this promise.
    runEligibility({ withingsUserId: patient.userid, carePeriodId, taskId, mode: 'submit' })
      .catch(() => { /* ack/timeout expected and ignored */ });
  }, [patient.userid, carePeriodId, taskId, runEligibility, captureBaseline]);

  const handleConfirmSubmit = useCallback(() => { submitCheck(); }, [submitCheck]);
  const handleRunPreview = useCallback(() => { runPreview(); }, [runPreview]);

  const didFireRef = useRef(false);
  useEffect(() => {
    aliveRef.current = true;
    // StrictMode double-invokes mount effects in dev; guard ensures the initial
    // preview fires exactly once. SUBMIT is strictly click-driven (handleConfirmSubmit)
    // — never a mount/auto effect — so StrictMode can't double-submit a real CMS call.
    if (!didFireRef.current) {
      didFireRef.current = true;
      runPreview();
    }
    return () => { aliveRef.current = false; if (capTimerRef.current) clearTimeout(capTimerRef.current); };
  }, [runPreview]);

  // Verdict from the poll: the first FRESH row (id not in baselineIds) that has
  // reached a terminal verdict. Same predicate the hook's refetchInterval uses.
  useEffect(() => {
    if (phase !== 'submit-loading' || !baselineIds) return;
    const fresh = freshTerminalRow(poll.data, baselineIds, 'eligibility');
    if (!fresh || settledRef.current) return;
    settledRef.current = true;
    if (capTimerRef.current) clearTimeout(capTimerRef.current);
    setResult(rowToResult(fresh));
    setPhase('result');
    requestAnimationFrame(() => { if (aliveRef.current) setResultVisible(true); });
    // WF12's side effects (auto-complete onb_eligibility_check, attach context)
    // land out-of-band — reconcile the task list + CMS roster past the SLA.
    scheduleInvalidate(qc, TASK_KEYS.all);
    scheduleInvalidate(qc, CMS_KEYS.all);
  }, [phase, baselineIds, poll.data, rowToResult, qc]);

  // Loading-step animation
  useEffect(() => {
    const isLoading = phase === 'preview-loading' || phase === 'submit-loading';
    if (!isLoading) return undefined;
    const steps = phase === 'preview-loading' ? PREVIEW_LOADING_STEPS : SUBMIT_LOADING_STEPS;
    const timers = steps.map((s, i) =>
      setTimeout(() => { if (aliveRef.current) setStepIndex(i); }, s.at)
    );
    return () => timers.forEach(clearTimeout);
  }, [phase]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  /* ── Derived ──────────────────────────────────────────────────────── */
  const eligible = result?.eligible === true;
  const sideEffects = result?.side_effects || {};
  const tasksCompleted = Array.isArray(sideEffects.tasks_completed) ? sideEffects.tasks_completed : [];
  const tasksFlagged = Array.isArray(sideEffects.tasks_flagged) ? sideEffects.tasks_flagged : [];
  const contextAttachedTo = Array.isArray(sideEffects.context_attached_to) ? sideEffects.context_attached_to : [];
  const exclusions = Array.isArray(result?.exclusions) ? result.exclusions : [];
  // WF12's alignment-task outcome: 'created' | 'deferred_labs_review' | 'gate_read_failed'
  // | 'skipped' | 'no_template' | 'error'. On the ordinary ordering (eligibility first) it
  // DEFERS, and without a row of its own the panel would say nothing at all about alignment
  // — sending the coordinator looking for a task that will not exist for ~3 weeks.
  const alignmentTask = sideEffects.alignment_task || null;
  const alignmentNote = alignmentTaskNote(alignmentTask);

  const resultMeta = result
    ? CmsResultCodes.labelFor(result.result_code) || {
        headline: result.display || (eligible ? 'Eligible' : 'Not eligible'),
        explainer: '',
        tone: eligible ? 'good' : 'bad',
      }
    : null;

  // For non-eligible result codes, treat as a clinical-outcome screen rather than an error.
  // Validation-failed gets its own moderate-tone screen with a different message.

  const patientName = [
    patient.firstname || patient.first_name,
    patient.lastname || patient.last_name,
  ].filter(Boolean).join(' ').trim();

  const checkedAtLabel = result?.checked_at ? formatTimestamp(result.checked_at) : null;

  /* ── Footer button config ─────────────────────────────────────────── */
  const handleDone = () => { onSaved?.(); onClose(); };

  /* ── Copy helpers ─────────────────────────────────────────────────── */
  const handleCopyVerdict = async () => {
    if (!result) return;
    const who = patientName || patient.userid;
    const verdict = eligible ? 'eligible' : (resultMeta?.headline || 'not eligible').toLowerCase();
    const date = result.checked_at ? new Date(result.checked_at).toLocaleDateString('en-US') : '';
    const mbiPart = result.mbi ? ` (${result.mbi})` : '';
    const summary = `${who}: CMS eligibility ${verdict}${mbiPart}${date ? ` on ${date}` : ''}`;
    try {
      await navigator.clipboard.writeText(summary);
      setCopied('verdict');
      setTimeout(() => { if (aliveRef.current) setCopied(null); }, 1800);
    } catch { /* clipboard unavailable */ }
  };

  const handleCopyMbi = async () => {
    if (!result?.mbi) return;
    try {
      await navigator.clipboard.writeText(result.mbi);
      setCopied('mbi');
      setTimeout(() => { if (aliveRef.current) setCopied(null); }, 1800);
    } catch { /* clipboard unavailable */ }
  };

  /* ── Render ───────────────────────────────────────────────────────── */
  return createPortal((
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: 580,
          width: '100%',
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '88vh',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '20px 24px 16px',
          borderBottom: '1px solid var(--wi-divider)',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          gap: 12, flexShrink: 0,
        }}>
          <div style={{ minWidth: 0 }}>
            <h2 className="header3" style={{ margin: 0 }}>CMS Eligibility Check</h2>
            <p className="detail1" style={{ margin: '4px 0 0', color: 'var(--wi-text-secondary)' }}>
              Medicare $check-eligibility · ACCESS Model
              {patientName && <> · {patientName}</>}
            </p>
          </div>
          <button
            className="modal-close"
            aria-label="Close"
            onClick={onClose}
            style={{ position: 'static' }}
          >×</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
          {phase === 'preview-loading' && (
            <LoadingPanel
              title="Building the FHIR Parameters payload for review…"
              stepIndex={stepIndex}
              steps={PREVIEW_LOADING_STEPS.map(s => s.label)}
              footnote="No CMS call yet — you'll confirm the payload before anything is sent."
            />
          )}

          {phase === 'submit-loading' && (
            <LoadingPanel
              title="Verifying Medicare eligibility with CMS…"
              stepIndex={stepIndex}
              steps={SUBMIT_LOADING_STEPS.map(s => s.label)}
              footnote="CMS may take up to 30 seconds to respond."
            />
          )}

          {phase === 'preview' && result && (
            <div style={{
              opacity: resultVisible ? 1 : 0,
              transition: 'opacity 200ms ease',
            }}>
              <VerdictBanner
                tone="info"
                headline="Ready to submit — review the payload"
                resultCode="preview"
                fhirDisplay={null}
                explainer="WF12 built the FHIR Parameters body it will POST to /Patient/$check-eligibility. Review it below, then click Confirm and submit to actually send it to CMS. Cancel to abort without any call."
              />

              {(() => {
                const sp = result.submitted_preview || {};
                const trackKey = (sp.track_code || 'eckm').toLowerCase();
                const trackLabel = TRACK_LABELS[trackKey] || trackKey.toUpperCase();
                const name = [sp.first_name, sp.last_name].filter(Boolean).join(' ');
                const conds = Array.isArray(sp.diagnosed_conditions) ? sp.diagnosed_conditions : [];
                return (
                  <>
                    <Section label="Patient identity (will be sent to CMS)">
                      <KeyValueGrid rows={[
                        ['Medicare MBI', sp.mbi || '—', { mono: true }],
                        ['Name', name || '—'],
                        ['Date of birth', sp.date_of_birth || '—'],
                        ['Sex', sp.gender || '—'],
                      ]} />
                    </Section>

                    <Section label="Track + participant">
                      <KeyValueGrid rows={[
                        ['ACCESS track', trackLabel],
                        ['Participant ID', sp.participant_id || '—', { mono: true }],
                        ['Payer ID', sp.payer_id || '—', { mono: true }],
                      ]} />
                    </Section>

                    <Section label={`Diagnosed conditions (${conds.length})`}>
                      {conds.length === 0 ? (
                        <p className="body2" style={{ margin: 0, color: 'var(--wi-text-placeholder)' }}>
                          No conditions on file — eligibility will be checked without a Condition resource. CMS may return <code>eligible-pending-diagnosis</code>.
                        </p>
                      ) : (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {conds.map((c, i) => {
                            const label = (typeof c === 'string') ? c : (c.label || c.display || c.name || c.condition || JSON.stringify(c)); // dual-shape: label objects | legacy strings
                            return (
                              <span
                                key={i}
                                className="detail1"
                                style={{
                                  padding: '3px 10px',
                                  borderRadius: 999,
                                  background: 'var(--wi-background-secondary)',
                                  color: 'var(--wi-text-secondary)',
                                  border: '1px solid var(--wi-divider)',
                                }}
                              >
                                {label}
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </Section>
                  </>
                );
              })()}

              {result.fhir_parameters && (
                <Section label="FHIR Parameters (raw JSON)">
                  <button
                    type="button"
                    onClick={() => setShowRawJson(v => !v)}
                    aria-expanded={showRawJson}
                    aria-controls="cms-eligibility-fhir-json"
                    className="detail1"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      background: 'transparent', border: '1px solid var(--wi-divider)',
                      cursor: 'pointer', padding: '4px 10px', borderRadius: 6,
                      color: 'var(--wi-text-secondary)', fontFamily: 'var(--wi-font-family)',
                      marginBottom: showRawJson ? 8 : 0,
                    }}
                  >
                    {showRawJson ? 'Hide raw JSON' : 'Show raw JSON'}
                  </button>
                  {showRawJson && (
                    <pre
                      id="cms-eligibility-fhir-json"
                      style={{
                        margin: 0, padding: 12,
                        background: 'var(--wi-background-primary)',
                        border: '1px solid var(--wi-divider)',
                        borderRadius: 8,
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                        fontSize: 11, lineHeight: 1.4,
                        color: 'var(--wi-text-primary)',
                        overflowX: 'auto', maxHeight: 320, overflowY: 'auto',
                        whiteSpace: 'pre',
                      }}
                    >
                      {JSON.stringify(result.fhir_parameters, null, 2)}
                    </pre>
                  )}
                </Section>
              )}
            </div>
          )}

          {phase === 'error' && (
            <ErrorPanel kind={errorKind} message={error} />
          )}

          {phase === 'result' && (!result || !resultMeta) && (
            <div style={{ padding: '24px 0' }}>
              <p className="body2" style={{ margin: 0, color: 'var(--wi-status-bad)' }}>
                CMS responded but we couldn&apos;t parse the result.
              </p>
              <p className="detail1" style={{ margin: '8px 0 0', color: 'var(--wi-text-secondary)' }}>
                The Tier 4 proxy returned an empty or malformed body. Re-run, or check the n8n execution log for WF12. Raw response: <code>{JSON.stringify(result)}</code>
              </p>
            </div>
          )}

          {phase === 'result' && result && resultMeta && (
            <div style={{
              opacity: resultVisible ? 1 : 0,
              transition: 'opacity 200ms ease',
            }}>
              {/* Verdict banner */}
              <VerdictBanner
                tone={resultMeta.tone}
                headline={resultMeta.headline}
                resultCode={result.result_code}
                fhirDisplay={result.display}
                explainer={resultMeta.explainer}
              />

              {/* Reason — failure detail from FHIR */}
              {result.reason && (
                <Section label="CMS detail">
                  <p className="body2" style={{ margin: 0, color: 'var(--wi-text-primary)' }}>
                    {result.reason}
                  </p>
                </Section>
              )}

              {/* Exclusions reported (advisory — eligible-pending-diagnosis case) */}
              {exclusions.length > 0 && (
                <Section label="Notes from CMS">
                  <Callout tone="moderate">
                    <p className="detail1" style={{ margin: '0 0 6px', color: 'var(--wi-text-primary-moderate)' }}>
                      {exclusions.length === 1
                        ? 'CMS attached one note to this response:'
                        : `CMS attached ${exclusions.length} notes to this response:`}
                    </p>
                    <ul className="body2" style={{ margin: 0, paddingLeft: 18, color: 'var(--wi-text-primary-moderate)' }}>
                      {exclusions.map((ex, i) => (
                        <li key={i}>
                          {typeof ex === 'string'
                            ? ex
                            : (ex.description || ex.display || ex.icd10 || JSON.stringify(ex))}
                          {typeof ex === 'object' && ex.icd10 && (
                            <span className="detail1" style={{ marginLeft: 6, color: 'var(--wi-text-secondary-moderate)' }}>
                              ({ex.icd10})
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </Callout>
                </Section>
              )}

              {/* MBI display (sensitive identifier — monospace + copy affordance) */}
              {result.mbi && (
                <Section label="Medicare Beneficiary Identifier (MBI)">
                  <MbiBlock mbi={result.mbi} copied={copied === 'mbi'} onCopy={handleCopyMbi} />
                </Section>
              )}

              {/* What happened in the system */}
              {(tasksCompleted.length > 0 || tasksFlagged.length > 0 || contextAttachedTo.length > 0 || alignmentNote) && (
                <Section label="System changes">
                  <SideEffects
                    completed={tasksCompleted}
                    flagged={tasksFlagged}
                    contextAttachedTo={contextAttachedTo}
                    alignmentNote={alignmentNote}
                  />
                </Section>
              )}

              {/* Timestamp */}
              {checkedAtLabel && (
                <p className="detail1" style={{ margin: '20px 0 0', color: 'var(--wi-text-placeholder)' }}>
                  Checked {checkedAtLabel}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex', gap: 10, justifyContent: 'space-between', alignItems: 'center',
          padding: '14px 24px',
          borderTop: '1px solid var(--wi-divider)',
          flexShrink: 0,
        }}>
          {/* Left: copy verdict (success only) */}
          <div>
            {phase === 'result' && result && (
              <button
                onClick={handleCopyVerdict}
                aria-label="Copy verdict to clipboard"
                title="Copy a one-line summary to paste into a chart note or chat"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  padding: '6px 8px', borderRadius: 6,
                  color: 'var(--wi-text-secondary)',
                  fontFamily: 'var(--wi-font-family)',
                }}
                className="detail1"
              >
                <CopyIcon size={14} />
                {copied === 'verdict' ? 'Copied' : 'Copy result'}
              </button>
            )}
          </div>

          {/* Right: actions */}
          <div style={{ display: 'flex', gap: 10 }}>
            {(phase === 'preview-loading' || phase === 'submit-loading') && (
              <button className="btn-ok" onClick={onClose}>Cancel</button>
            )}
            {phase === 'preview' && (
              <>
                <button className="btn-ok" onClick={onClose}>Cancel</button>
                <button className="btn-primary" onClick={handleConfirmSubmit}>Confirm and submit to CMS</button>
              </>
            )}
            {phase === 'error' && (
              <>
                <button className="btn-ok" onClick={onClose}>Close</button>
                <button className="btn-primary" onClick={handleRunPreview}>Retry</button>
              </>
            )}
            {phase === 'result' && (
              <button className="btn-primary" onClick={handleDone}>Done</button>
            )}
          </div>
        </div>
      </div>
    </div>
  ), document.body);
}

/* ══════════════════════════════════════════════════════════════════════
 * Subcomponents
 * ════════════════════════════════════════════════════════════════════ */

function ErrorPanel({ kind, message }) {
  // kind: 'network' | 'timeout' | 'auth' | 'validation' | 'ineligible'
  const meta = {
    network: {
      tone: 'bad',
      title: 'We couldn\'t reach CMS',
      body: 'The CMS FHIR service didn\'t respond at all. This is usually transient infrastructure — try again in a moment. If it persists, check the n8n deploy and contact platform-eng.',
    },
    timeout: {
      tone: 'moderate',
      title: 'CMS is taking longer than usual',
      body: 'CMS accepted the request but didn\'t finish within the 24-second window. The verification may still be running on their side — wait 10 seconds and retry. If it keeps timing out, file a ticket with CMS support.',
    },
    auth: {
      tone: 'bad',
      title: 'CMS rejected our credentials',
      body: 'Our OAuth bearer token was missing, expired, or rejected by CMS. This is a system config issue, not a patient issue — contact platform-eng before retrying.',
    },
    validation: {
      tone: 'moderate',
      title: 'Missing required patient data',
      body: 'We can\'t send the request to CMS because the patient record is missing or has malformed required fields — usually the Medicare MBI or date of birth. Update the patient / insurance record before retrying.',
    },
    ineligible: {
      tone: 'bad',
      title: 'Patient is not Medicare-eligible',
      body: 'CMS reports this patient is not currently eligible for ACCESS Model services.',
    },
  }[kind] || {
    tone: 'bad',
    title: 'Eligibility check failed',
    body: 'An unexpected error occurred.',
  };

  return (
    <div>
      <VerdictBanner
        tone={meta.tone}
        headline={meta.title}
        resultCode={null}
        fhirDisplay={null}
        explainer={meta.body}
      />
      {message && (
        <Section label="Detail">
          <p className="body2" style={{ margin: 0, color: 'var(--wi-text-secondary)' }}>{message}</p>
        </Section>
      )}
    </div>
  );
}

/* KeyValueGrid — 2-column label/value rows for the preview "what's being sent
   to CMS" panels. Values that look like identifiers (MBI / participant id) can
   opt into a monospace font via `{ mono: true }`. */
function KeyValueGrid({ rows }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(140px, 1fr) 2fr',
        rowGap: 6,
        columnGap: 12,
        padding: '10px 12px',
        background: 'var(--wi-background-primary)',
        borderRadius: 8,
        border: '1px solid var(--wi-divider)',
      }}
    >
      {rows.map(([label, value, opts], i) => (
        <React.Fragment key={i}>
          <span className="detail1" style={{ color: 'var(--wi-text-secondary)' }}>{label}</span>
          <span
            className="body2"
            style={{
              color: 'var(--wi-text-primary)',
              fontFamily: opts?.mono ? 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' : undefined,
              letterSpacing: opts?.mono ? '0.02em' : undefined,
              wordBreak: 'break-all',
            }}
          >
            {value}
          </span>
        </React.Fragment>
      ))}
    </div>
  );
}

function MbiBlock({ mbi, copied, onCopy }) {
  const formatted = formatMbi(mbi);
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '10px 12px',
        background: 'var(--wi-background-primary)',
        borderRadius: 8,
        border: '1px solid var(--wi-divider)',
      }}
    >
      <span
        className="body2"
        style={{
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
          letterSpacing: '0.04em',
          color: 'var(--wi-text-primary)',
          flex: 1,
        }}
      >
        {formatted}
      </span>
      <span
        className="detail1"
        style={{ color: 'var(--wi-text-placeholder)' }}
      >
        sensitive
      </span>
      <button
        onClick={onCopy}
        aria-label="Copy MBI"
        title="Copy MBI to clipboard"
        style={{
          background: 'transparent', border: 'none', cursor: 'pointer',
          padding: 6, borderRadius: 6, display: 'inline-flex', alignItems: 'center',
          color: copied ? 'var(--wi-status-good)' : 'var(--wi-text-secondary)',
        }}
      >
        {copied ? <CheckIcon size={14} /> : <CopyIcon size={14} />}
      </button>
    </div>
  );
}

function SideEffects({ completed, flagged, contextAttachedTo, alignmentNote }) {
  return (
    <div style={{ background: 'var(--wi-background-primary)', borderRadius: 8, padding: 12 }}>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {completed.map(code => (
          <SideEffectRow key={`done-${code}`} kind="done" text={`${labelFor(code)} marked complete`} />
        ))}
        {flagged.map(code => (
          <SideEffectRow key={`flag-${code}`} kind="flagged" text={`${labelFor(code)} flagged for manual review`} />
        ))}
        {contextAttachedTo.map(code => (
          <SideEffectRow key={`ctx-${code}`} kind="info" text={`${labelFor(code)} created`} />
        ))}
        {alignmentNote && contextAttachedTo.length === 0 && (
          <SideEffectRow key="alignment-note" kind="info" text={alignmentNote} />
        )}
      </ul>
    </div>
  );
}

function SideEffectRow({ kind, text }) {
  const meta = {
    done:    { color: 'var(--wi-status-good)',     glyph: <CheckIcon size={14} /> },
    flagged: { color: 'var(--wi-status-moderate)', glyph: <WarnIcon size={14} /> },
    info:    { color: 'var(--wi-status-info)',     glyph: <InfoIcon size={14} /> },
  }[kind];
  return (
    <li style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
      <span style={{ color: meta.color, marginTop: 1, flexShrink: 0 }}>{meta.glyph}</span>
      <span className="body2" style={{ color: 'var(--wi-text-primary)' }}>{text}</span>
    </li>
  );
}

/* ── Icons (inline SVG, currentColor-driven) ────────────────────────── */
function CopyIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}
function CheckIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}
function WarnIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}
function InfoIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  );
}

/* ── Formatters ─────────────────────────────────────────────────────── */
function formatTimestamp(iso) {
  try {
    return new Intl.DateTimeFormat('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(new Date(iso)).replace(',', ' at');
  } catch {
    return iso;
  }
}

function formatMbi(mbi) {
  if (!mbi) return '';
  const clean = String(mbi).toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (clean.length !== 11) return clean;
  return `${clean.slice(0, 4)}-${clean.slice(4, 7)}-${clean.slice(7, 11)}`;
}
